import { LiveAnnouncer } from '@angular/cdk/a11y';
import { ListRange } from '@angular/cdk/collections';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
    Inject,
    Injectable,
    InjectionToken,
    OnDestroy,
    Optional,
} from '@angular/core';
import { AppStateFacade } from '@app-state';
import {
    buildParentMap,
    emptyFolderContainers,
    getParentIds,
    patchContainers,
    trimExpandedFolders,
} from '@components/ui-folder-tree/helpers';
import {
    IFolder,
    IFolderContext,
    ODataResponse,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import { FoldersService } from '@services';
import { VirtualScrollItemStatus } from '@uipath/angular/directives/ui-virtual-scroll-range-loader';

import cloneDeep from 'lodash-es/cloneDeep';
import {
    BehaviorSubject,
    combineLatest,
    Observable,
    of,
    Subject,
} from 'rxjs';
import {
    finalize,
    map,
    retry,
    skip,
    takeUntil,
    tap,
} from 'rxjs/operators';

export type FolderParentId = number | null; // Root folders have a null parentId.

export interface IFolderContainer {
    loading: VirtualScrollItemStatus;
    folderData?: IFolder;
    fakeId: number;
}
export interface LoadingState {
    id?: number;
    value: boolean;
}

export interface IFolderSource {
    hasSwitchCapability: boolean;
    getFolders: (expandedIds: number[], take: number, skip: number) => Observable<ODataResponse<IFolder>>;
    getFolderContext?: (folderId: number | null, take: number, relativeSkip: number) => Observable<IFolderContext>;
    searchFolders?: (skip: number, take: number, searchText: string) => Observable<ODataResponse<IFolder>>;
}

export const FOLDER_TREE_SOURCE = new InjectionToken<IFolderSource>('Folder-Tree-Source');
export const FOLDER_FETCH_SIZE = 50;
export const INITIAL_SKIP_OFFSET = -FOLDER_FETCH_SIZE / 2;

@Injectable()
export class FolderTreeStateService implements OnDestroy {
    public parentIdOfExpandedId = new Map<number, FolderParentId>();
    public highlighted$ = new BehaviorSubject<IFolder | null>(null);
    public activeIndex = 0;
    public initialLoading$ = new BehaviorSubject(true);
    public loadingFolder$ = new BehaviorSubject<LoadingState>({ value: false });
    public searching$ = new BehaviorSubject(false);

    public get isInSearchMode() {
        return !!this.searchValue;
    }

    public get searchValue() {
        return this._searchValue$.value;
    }

    public set searchValue(value: string) {
        this._searchValue$.next(value);
        if (!value) {
            this.refresh();
        } else {
            this.searching$.next(true);
            this._searchFolders(value, FOLDER_FETCH_SIZE, 0);
        }
        this.highlighted$.next(null);
    }

    private _searchValue$ = new BehaviorSubject('');
    private _destroyed$ = new Subject();
    private _patchStarted$ = new Subject();
    private _treeContainers$ = new BehaviorSubject<IFolderContainer[]>([]);
    private _searchContainers$ = new BehaviorSubject<IFolderContainer[]>([]);
    private _containers$ = new BehaviorSubject<IFolderContainer[]>([]);

    public get selectedFolderId() {
        if (!this._folderSource.hasSwitchCapability) {
            return null;
        }
        const folderManager = this._appState.user.folder;
        return folderManager.hasAny ? folderManager.value!.id : null;
    }

    public get selectedFolder() {
        if (this.selectedFolderId === null) {
            return null;
        }
        return this._appState.user.folder.value;
    }

    public get treeContainers() {
        return this._treeContainers$.value;
    }

    public set treeContainers(containers: IFolderContainer[]) {
        this._treeContainers$.next(containers);
    }

    public get searchContainers() {
        return this._searchContainers$.value;
    }

    public set searchContainers(containers: IFolderContainer[]) {
        this._searchContainers$.next(containers);
    }

    public get containers$() {
        return this._containers$;
    }

    public get containers() {
        return this._containers$.value;
    }

    constructor(
        @Optional()
        @Inject(FOLDER_TREE_SOURCE)
        private _folderSource: IFolderSource,
        private _appState: AppStateFacade,
        private _translateService: TranslateService,
        private _liveAnnouncer: LiveAnnouncer,
        folderService: FoldersService,
    ) {
        if (!this._folderSource) {
            this._folderSource = folderService;
        }

        this.getInitialContext()
            .pipe(
                takeUntil(this._destroyed$),
            ).subscribe(this._initialContextSetup);

        combineLatest([
            this._treeContainers$,
            this._searchContainers$,
            this._searchValue$,
        ]).pipe(
            takeUntil(this._destroyed$),
        ).subscribe(([treeContainers, searchContainers, searchValue]) =>
            this._containers$.next(!!searchValue ? searchContainers : treeContainers),
        );
    }

    public getInitialContext(): Observable<IFolderContext> {
        if (!this._folderSource.hasSwitchCapability) {
            return this._folderSource.getFolders([], FOLDER_FETCH_SIZE, 0).pipe(
                map(({ total: count, data: folders }) => ({ count, folders, index: 0, ancestors: [] })),
            );
        }
        return this._folderSource.getFolderContext!(this.selectedFolderId, FOLDER_FETCH_SIZE, INITIAL_SKIP_OFFSET);
    }

    public ngOnDestroy() {
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    public switchToHighlighted = () => {
        const highlightedFolder = this.highlighted$.value!;

        if (
            !this._folderSource.hasSwitchCapability ||
            !highlightedFolder.isSelectable ||
            highlightedFolder.id === this.selectedFolderId
        ) { return of(false); }

        return this._appState.user.folder
            .set(highlightedFolder.id).pipe(
                tap(() => {
                    const folder = this._appState.user.folder.value!;
                    if (
                        this.isInSearchMode &&
                        folder.folderContext != null
                    ) {
                        this._initialContextSetup(folder.folderContext, 0);
                    }
                }),
            );
    }

    public isExpanded = (nodeId: number) =>
        this.parentIdOfExpandedId.has(nodeId)

    public toggleExpand(toggledFolder: IFolder, toggledFolderIndex: number, ev?: Event) {
        if (ev) { ev.stopPropagation(); }
        const { id: toggledId } = toggledFolder;
        this.loadingFolder$.next({ id: toggledId, value: true });

        if (this.isExpanded(toggledId)) {
            this.parentIdOfExpandedId.delete(toggledId);
        } else {
            this.parentIdOfExpandedId.set(toggledId, toggledFolder.parentId);
        }

        const currentFolder = this.containers.find(f => !!f.folderData && f.folderData!.id === toggledId)!.folderData!;

        this._announce(currentFolder);

        const shouldRemoveHighlight = this.highlighted$.value &&
            getParentIds(this.treeContainers, this.highlighted$.value.id).includes(toggledId);

        this._patch(
            Math.max(0, toggledFolderIndex - FOLDER_FETCH_SIZE / 2),
            FOLDER_FETCH_SIZE,
            () => true,
        ).pipe(
            finalize(() => this.loadingFolder$.next({ value: false })),
        ).subscribe(() => {
            if (shouldRemoveHighlight) {
                this.highlighted$.next(null);
            }
        });
    }

    public rangeLoad({ start, end }: ListRange) {
        if (!!this._searchValue$.value) {
            this._searchFolders(this.searchValue, FOLDER_FETCH_SIZE, start);
            return;
        }

        this.treeContainers
            .slice(start, end + 1)
            .forEach(f => f.loading = VirtualScrollItemStatus.pending);
        this._treeContainers$.next(this.treeContainers);

        this._patch(start, end - start + 1, count => count !== this.treeContainers.length)
            .subscribe(wasInvalidated => {
                if (wasInvalidated) {
                    const range = start > this.activeIndex ?
                        { start: Math.max(0, start - FOLDER_FETCH_SIZE), end: start - 1 } :
                        { start: end + 1, end: Math.min(this.treeContainers.length - 1, end + FOLDER_FETCH_SIZE) };
                    this.rangeLoad(range);
                }
            });
    }

    public refresh() {
        if (this.initialLoading$.value) {
            return;
        }

        this._patch(
            Math.max(0, this.activeIndex - 5),
            FOLDER_FETCH_SIZE,
            count => count !== this.treeContainers.length,
        ).pipe(
            takeUntil(this._patchStarted$),
        ).subscribe();
    }

    public patchFolder(changes: Partial<IFolder> & { id: number }) {
        const folder = this.folderById(changes.id);
        if (!folder) { return; }

        const folderData = folder.folderData;
        Object.assign(folderData, changes);

        this._treeContainers$.next(this.treeContainers);

        if (
            this.highlighted$.value &&
            this.highlighted$.value.id === changes.id
        ) {
            this.highlighted$.next(cloneDeep(folderData!));
        }
    }

    public folderById(id: number) {
        const folderIndex = this.folderIndexById(id);
        if (folderIndex === -1) { return undefined; }

        return this.containers[folderIndex];
    }

    public folderIndexById(id: number) {
        return this.containers.findIndex(c => c.folderData && c.folderData.id === id);
    }

    public navigate(direction: 'up' | 'down' | 'left' | 'right', vs: CdkVirtualScrollViewport) {
        if (!this.containers.length) {
            return;
        }

        const currentHighlight = this.highlighted$.value;

        if (!currentHighlight) {
            const firstRenderedIndex = vs.getRenderedRange().start;
            const firstRendered = this.containers[firstRenderedIndex] && this.containers[firstRenderedIndex].folderData;

            if (!firstRendered) { return; }

            this.highlighted$.next(firstRendered!);
            this._announce(firstRendered!);
            vs.scrollToIndex(Math.max(firstRenderedIndex - 3, 0));
            return;
        }

        const currentIndex = Math.max(
            this.containers.findIndex(c => !!c.folderData && c.folderData.id === currentHighlight.id),
            0,
        );
        let nextIndex = currentIndex;

        const currentFolder = this.containers[currentIndex].folderData!;

        if (direction === 'right') {
            if (currentFolder.hasChildren && !this.isExpanded(currentFolder.id)) {
                this.toggleExpand(currentFolder, currentIndex);
                return;
            }

            direction = 'down';
        } else if (direction === 'left') {
            if (this.isExpanded(currentFolder.id)) {
                this.toggleExpand(currentFolder, currentIndex);
                return;
            }

            const parentIndex = this.containers.findIndex(c => !!c.folderData && c.folderData.id === currentFolder.parentId);

            if (parentIndex !== -1) {
                nextIndex = parentIndex;
            } else {
                direction = 'up';
            }
        }

        nextIndex +=
            direction === 'up'
                ? -1
                : direction === 'down'
                    ? 1
                    : 0;

        nextIndex = Math.min(Math.max(nextIndex, 0), this.containers.length - 1);

        const nextFolder = this.containers[nextIndex].folderData;
        if (!nextFolder) {
            return;
        }

        vs.scrollToIndex(Math.max(nextIndex - 3, 0));
        this.highlighted$.next(this.containers[nextIndex].folderData!);
        this._announce(nextFolder);
    }

    private _patch = (skipItems: number, take: number, invalidate = (_: number) => false) => {
        this._patchStarted$.next();

        const fetch$ = this._folderSource.getFolders(
            trimExpandedFolders(this.parentIdOfExpandedId),
            take,
            skipItems,
        );

        return fetch$.pipe(
            retry(1),
            map(({ data: folders, total: count }) => {
                const shouldInvalidate = invalidate(count);
                const newContainers = shouldInvalidate ? emptyFolderContainers(count) : this.treeContainers;
                patchContainers(folders, skipItems, newContainers);
                this.treeContainers = newContainers;
                this._checkHighlightValid();
                return shouldInvalidate;
            }),
            takeUntil(this._destroyed$),
        );
    }

    private _checkHighlightValid() {
        if (!this.highlighted$.value) {
            return;
        }

        const highlightedFolder = this.folderById(this.highlighted$.value!.id);
        if (!highlightedFolder) {
            this.highlighted$.next(null);
        }
    }

    private _initialContextSetup = ({ ancestors, index: highlightIndex, folders, count }: IFolderContext, offset = INITIAL_SKIP_OFFSET) => {
        const containers = emptyFolderContainers(count);
        patchContainers(folders, Math.max(0, highlightIndex + offset), containers);
        this.treeContainers = containers;
        this.parentIdOfExpandedId = buildParentMap(ancestors);
        this.activeIndex = Math.max(0, highlightIndex - 5);

        const folderData = this.treeContainers[highlightIndex]
            ? this.treeContainers[highlightIndex].folderData
            : null;
        if (folderData) {
            this.highlighted$.next(folderData);
        }

        this.initialLoading$.next(false);
    }

    private _searchFolders = (searchTerm: string, fetchSize: number, skipItems = 0) => {
        this.searchContainers
            .slice(skipItems, skipItems + fetchSize)
            .forEach(f => f.loading = VirtualScrollItemStatus.pending);

        this._folderSource.searchFolders!(skipItems, fetchSize, searchTerm)
            .pipe(
                takeUntil(this._searchValue$.pipe(skip(1))),
                finalize(() => this.searching$.next(false)),
            )
            .subscribe(({ total, data }) => {
                data.forEach(f => {
                    f.level = 1;
                    f.isSelectable = true;
                });
                const flushContainers = skipItems === 0 || total !== this.searchContainers.length;
                const containers = flushContainers ? emptyFolderContainers(total) : this.searchContainers;
                patchContainers(data, skipItems, containers);
                this.searchContainers = containers;
            });
    }

    private _announce({ displayName, level, fullyQualifiedName, hasChildren, id }: IFolder) {
        const params = {
            folder: this.isInSearchMode ? fullyQualifiedName : displayName,
            level,
        };

        const key = !!this.searchValue
            ? 'folder_tree_search_result'
            : !hasChildren
                ? 'folder_tree_folder_no_children'
                : this.isExpanded(id)
                    ? 'folder_tree_folder_expanded'
                    : 'folder_tree_folder_collapsed';

        this._liveAnnouncer.announce(
            this._translateService.instant(
                key,
                params,
            ),
        );
    }
}
